import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getCategoryBySlug, getPostsByCategory, CATEGORIES, type BlogCategory, type BlogPost } from "@/data/blog";
import { BlogShell } from "@/components/blog/BlogLayout";
import { BlogCard } from "@/components/blog/BlogCard";
import { NewsletterBox } from "@/components/blog/ArticleCTA";

export const Route = createFileRoute("/blog/categoria/$slug")({
  loader: ({ params }) => {
    const cat = getCategoryBySlug(params.slug);
    if (!cat) throw notFound();
    return { category: cat, posts: getPostsByCategory(params.slug) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const url = `https://www.flash-web.ch/blog/categoria/${params.slug}`;
    const title = `${loaderData.category.name} — Blog FlashWeb`;
    return {
      meta: [
        { title },
        { name: "description", content: loaderData.category.description },
        { property: "og:title", content: title },
        { property: "og:description", content: loaderData.category.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <BlogShell>
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-3xl font-bold">Categoria non trovata</h1>
        <Link to="/blog" className="mt-6 inline-block text-primary hover:underline">Torna al blog</Link>
      </div>
    </BlogShell>
  ),
  errorComponent: ({ error, reset }) => (
    <BlogShell>
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Errore: {error.message}</h1>
        <button onClick={reset} className="mt-4 text-primary hover:underline">Riprova</button>
      </div>
    </BlogShell>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const { category, posts } = Route.useLoaderData() as { category: BlogCategory; posts: BlogPost[] };
  return (
    <BlogShell>
      <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-6 pt-8 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-2">
          <li><Link to="/" className="hover:text-foreground">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
          <li aria-hidden>/</li>
          <li className="text-foreground font-medium">{category.name}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-7xl px-6 py-10">
        <span className="text-xs font-semibold uppercase tracking-wider text-primary">Categoria</span>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">{category.name}</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">{category.description}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {CATEGORIES.filter((c) => c.slug !== category.slug).map((c) => (
            <Link
              key={c.slug}
              to="/blog/categoria/$slug"
              params={{ slug: c.slug }}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-foreground"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16">
        {posts.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
        ) : (
          <p className="text-muted-foreground">Nessun articolo in questa categoria, per ora.</p>
        )}
        <div className="mt-16"><NewsletterBox /></div>
      </section>
    </BlogShell>
  );
}
